import { Alert, Box, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  BlockMiniMap,
  HousekeepingPlanResponse,
  housekeepingPlanService,
  occupancyService,
  PositionDto,
  YardBlockDto,
  yardBlockService,
} from '@tom-ui/yard'
import { ConfirmationDialog } from '@tom-ui/ui'
import { appStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { WorkInstructionUIStore } from '../../stores/work-instruction.ui-store'

interface ApiErrorShape {
  response?: { status?: number; data?: { detail?: string } }
}

interface Props {
  store: WorkInstructionUIStore
}

// Minimal amend-mode dialog. Only the destination (block + areas) is editable per
// the v1 scope; name / sequencing / row-fill render as read-only metadata so the
// planner can see what they're amending without being able to change those fields.
// Loads plan detail + yard blocks + occupancies on open. The backend re-validates
// destination capacity against the surviving Dispatched moves, so the dialog can
// stay a thin client without recomputing free-slot math locally.
export const AmendHousekeepingPlanDialog = observer(({ store }: Props) => {
  const { t } = useTranslate()

  const planId = store.selectedHousekeepingGroupId
  const isOpen = store.isAmendHousekeepingPlanDialogOpen
  const pendingMovesCount = store.pendingHousekeepingMovesCount

  const [plan, setPlan] = useState<HousekeepingPlanResponse | null>(null)
  const [blocks, setBlocks] = useState<YardBlockDto[]>([])
  const [occupancyByBlockId, setOccupancyByBlockId] = useState<Record<string, PositionDto[]>>({})
  const [selectedBlockId, setSelectedBlockId] = useState<string>('')
  const [selectedPositions, setSelectedPositions] = useState<PositionDto[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Reset transient state every time the dialog opens — without this the
  // previously-selected positions stick around when a planner opens the dialog
  // twice in a row for different plans.
  useEffect(() => {
    if (!isOpen || !planId) return

    let cancelled = false
    setIsLoading(true)
    Promise.all([
      housekeepingPlanService.getPlanById(planId),
      yardBlockService.getYardBlocks(),
      occupancyService.getOccupancies(),
    ])
      .then(([planDetail, blockList, occupancies]) => {
        if (cancelled) return
        setPlan(planDetail)
        setBlocks(blockList ?? [])

        const map: Record<string, PositionDto[]> = {}
        for (const blockEntry of occupancies) {
          const positions: PositionDto[] = []
          for (const jwp of blockEntry.journeyWithPositions) {
            const ps = jwp.positions ?? []
            for (const p of ps) {
              positions.push(p)
            }
          }
          map[blockEntry.blockId] = positions
        }
        setOccupancyByBlockId(map)

        setSelectedBlockId(planDetail.destinationBlockId ?? '')
        setSelectedPositions(planDetail.destinationAreas ?? [])
      })
      .catch(() => {
        if (cancelled) return
        appStore.setShowAlert(
          'error',
          t('internalMoves.amendDialog.loadError', 'Could not load housekeeping plan.'),
        )
        store.closeAmendHousekeepingPlanDialog()
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, planId, store, t])

  // Block change resets the area picks — selections belong to a specific block.
  useEffect(() => {
    if (!plan) return
    if (selectedBlockId === plan.destinationBlockId) {
      setSelectedPositions(plan.destinationAreas ?? [])
    } else {
      setSelectedPositions([])
    }
  }, [selectedBlockId, plan])

  const selectedBlock = useMemo<YardBlockDto | undefined>(
    () => blocks.find(b => b.id === selectedBlockId),
    [blocks, selectedBlockId],
  )

  const occupiedPositions = useMemo<PositionDto[]>(
    () => (selectedBlock ? (occupancyByBlockId[selectedBlock.id] ?? []) : []),
    [selectedBlock, occupancyByBlockId],
  )

  const canSubmit =
    !!plan && selectedBlockId !== '' && selectedPositions.length >= pendingMovesCount

  const handleSubmit = async () => {
    if (!planId || !canSubmit) return
    setIsSubmitting(true)
    try {
      await housekeepingPlanService.amendPlan(planId, {
        destinationBlockId: selectedBlockId,
        destinationAreas: selectedPositions,
      })
      appStore.setShowAlert(
        'success',
        t('internalMoves.amendDialog.success', 'Housekeeping plan amended.'),
      )
      store.closeAmendHousekeepingPlanDialog()
    } catch (error) {
      const detail = (error as ApiErrorShape)?.response?.data?.detail
      appStore.setShowAlert(
        'error',
        detail ?? t('internalMoves.amendDialog.error', 'Could not amend housekeeping plan.'),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    store.closeAmendHousekeepingPlanDialog()
  }

  return (
    <ConfirmationDialog
      open={isOpen}
      title={t('internalMoves.amendDialog.title', 'Amend housekeeping plan')}
      primaryActionText={t('internalMoves.amendDialog.confirm', 'Save changes')}
      primaryActionDisabled={!canSubmit || isLoading}
      closeLabel={t('internalMoves.amendDialog.dismiss', 'Cancel')}
      maxWidth='md'
      isLoading={isSubmitting}
      onClose={handleClose}
      onConfirm={handleSubmit}
    >
      {isLoading || !plan ? (
        <Typography variant='body2'>
          {t('internalMoves.amendDialog.loading', 'Loading plan details…')}
        </Typography>
      ) : (
        <Stack spacing={2}>
          <Alert severity='info'>
            {t(
              'internalMoves.amendDialog.hint',
              '{pending} pending move(s) will be redirected. In-progress and completed moves are not affected.',
              { pending: pendingMovesCount },
            )}
          </Alert>

          <Stack direction='row' spacing={2}>
            <TextField
              label={t('internalMoves.amendDialog.planName', 'Plan name')}
              value={plan.name ?? ''}
              InputProps={{ readOnly: true }}
              fullWidth
              size='small'
            />
            <TextField
              label={t('internalMoves.amendDialog.sequencing', 'Sequencing strategy')}
              value={plan.sequencingStrategy}
              InputProps={{ readOnly: true }}
              fullWidth
              size='small'
            />
            <TextField
              label={t('internalMoves.amendDialog.rowFill', 'Row/fill direction')}
              value={plan.rowFillDirection}
              InputProps={{ readOnly: true }}
              fullWidth
              size='small'
            />
          </Stack>

          <TextField
            label={t('internalMoves.amendDialog.destinationBlock', 'Destination block')}
            value={selectedBlockId}
            onChange={e => setSelectedBlockId(e.target.value)}
            select
            fullWidth
            size='small'
          >
            {blocks.map(b => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>

          {selectedBlock && (
            <Box>
              <BlockMiniMap
                block={selectedBlock}
                occupiedPositions={occupiedPositions}
                selectedPositions={selectedPositions}
                onSelectionChange={setSelectedPositions}
              />
            </Box>
          )}

          <Typography variant='caption'>
            {t(
              'internalMoves.amendDialog.capacity',
              'Selected {selected} slot(s) for {pending} pending move(s).',
              {
                selected: selectedPositions.length,
                pending: pendingMovesCount,
              },
            )}
          </Typography>
        </Stack>
      )}
    </ConfirmationDialog>
  )
})
